import classNames from 'classnames';
import { useEffect, useState } from 'react';
import type { LocalFeedObject, RemoteFeedObject } from 'vcs-common';

import { MeetChatPanel } from '../../components/meet/chat';
import { GroupConnectionRequestNotification } from '../../components/meet/group-connection-request-notification';
import { GroupParticipantsPanel } from '../../components/meet/group-participants-panel';
import { MeetLayoutVideosTopPanelBottom } from '../../components/meet/meet-layout-videos-top-panel-bottom';
import { ScreenShareViewer } from '../../components/meet/screen-share-viewer';
import { SettingPanel } from '../../components/meet/settings-panel';
import { PsyToolsId } from '../../components/meet/tools-panel/types';
import { VideoPanel } from '../../components/meet/video-panel';
import { EmotionsGroupClient } from '../../components/tools/detector-emo/emotions-group-client';
import { EmotionsGroupPanel } from '../../components/tools/detector-emo/emotions-group-panel';
import { MacCardsClient } from '../../components/tools/mac-cards/mac-card-client';
import { MacGroupPanel } from '../../components/tools/mac-cards/mac-group-panel';
import { Line } from '../../components/tools/clouds/types';
import {
  getExpectedParticipantIds,
  getParticipantDisplayNames,
  getRegularVideos,
  getScreenShareFeed,
  getScreenShareOwnerDisplayName,
  getVideoPanelDisplayMode,
} from '../../features/meet/meet.adapters';
import { useMeetModeration } from '../../features/meet/MeetModerationProvider';
import { useMeetPermissionsContext } from '../../features/meet/MeetPermissionsProvider';
import { useMeetTools } from '../../features/meet/MeetToolsProvider';
import type { IClient, IConsultation } from '../../store/types';
import type { SupervisorNotification } from '../../features/meet/useMeetSupervisor';

import { Breakpoint } from '../../utils/breakpoint';

import { MeetToolsClient } from './meet-tools-client';
import { MeetToolsPsy } from './meet-tools-psy';
import type { MeetChatMessage } from '../../features/meet/chat/types';

function ExpandableClientPanel({
  expanded,
  onToggle,
  children,
}: {
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="h-full w-full flex flex-col bg-white rounded-[5px] md:rounded-[15px] overflow-hidden">
      <div className="flex-shrink-0 flex items-center justify-end gap-2 px-4 py-2 border-b border-[#E5E5E5]">
        <button
          type="button"
          onClick={onToggle}
          title={expanded ? 'Свернуть панель' : 'Расширить панель'}
          className="flex items-center gap-1.5 text-[#2C3038] hover:text-[#585858] text-sm font-manrope"
          aria-label={expanded ? 'Свернуть панель' : 'Расширить панель'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
          </svg>
          {expanded ? 'Свернуть' : 'Расширить'}
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden p-4">{children}</div>
    </div>
  );
}

interface MeetRoomSceneProps {
  session: {
    roomName: string;
    consultation?: IConsultation;
    client?: IClient;
    users: unknown[];
    joined: boolean;
    localVideo: LocalFeedObject | null;
    localParticipantIdentity: string;
    isPsychologist: boolean;
    isClientConnected: boolean;
    isLocalActiveSpeaker: boolean;
  };
  layout: {
    breakpoint: Breakpoint;
    formFactor: string;
    hideToolsPanelForGroupCall: boolean;
  };
  controls: {
    closeMeet: () => void | Promise<void>;
    leaveConferenceOnly: () => void;
    closeActiveTool: () => void;
    onOpenSettingsClick: () => void;
    onBackgroundBlurClick: () => void;
    onSupervisorClick: () => void;
    onScreenShareClick: () => void;
    onOpenMacDeckModal: () => void;
    onOpenMacCardsForGroup: () => void;
    onOpenEmotionsForGroup: () => void;
    onToggleRaisedHand: () => void;
  };
  supervisor: {
    statusActive: boolean;
    isSupervisorAllowed: boolean;
    supervisorRemaining?: number;
    isSupervisorBalanceWarning: boolean;
    isTooltipVisible: boolean;
    supervisorNotification: SupervisorNotification | null;
    setSupervisorNotification: (value: SupervisorNotification | null) => void;
    supervisorSend: boolean;
    setSupervisorSend: (value: boolean) => void;
    notificationClosed: boolean;
    setNotificationClosed: (value: boolean) => void;
    isSupervisorLoading: boolean;
  };
  videoRoomActions: {
    backgroundMode: 'none' | 'blur' | 'image';
    isBackgroundSupported: boolean;
    toggleBackground: (mode: 'blur' | 'image', src?: string) => void;
    stopVideoTrack?: () => void;
    sendSupervisorResponse?: (accepted: boolean) => void;
    sendMacCardsOpen?: (data: {
      show: boolean;
      cover: string;
      color: string;
      result: { name: string; value: string[] };
    }) => void;
    sendMacCardsClose?: () => void;
    sendMacSelectCards?: (result: Array<{ id: number; img: string; selected: boolean }>) => void;
    sendCloudsOpen?: () => void;
    sendCloudsClose?: () => void;
    sendCloudsDrawLine?: (line: Line) => void;
    sendCloudsDrawLines?: (lines: Line[]) => void;
    sendCloudsClearDrawing?: () => void;
    sendCloudsSelectImage?: (imageIndex: number) => void;
    sendCloudsClientFinished?: () => void;
    sendCloudsUndo?: (linesCount: number) => void;
    sendEmotionsOpen?: () => void;
    sendEmotionsClose?: () => void;
    sendEmotionsSelect?: (result: { category: string[]; emo: string[] }) => void;
    sendColorCodeOpen?: () => void;
    sendColorCodeClose?: () => void;
    sendColorCodeResult?: (colors: string[]) => void;
    sendWheelOpen?: (values: number[]) => void;
    sendWheelClose?: () => void;
    sendWheelChange?: (values: number[]) => void;
    sendWheelResult?: (values: number[]) => void;
    sendDpdgOpen?: (payload?: { initialSync?: Record<string, unknown> }) => void;
    sendDpdgClose?: () => void;
    sendDpdgMessage?: (action: string, data: Record<string, unknown>) => void;
    sendDpdgVideoControl?: (payload: { type: string; time?: number }) => void;
    sendRequestMuteParticipant?: (identity: string) => Promise<boolean>;
    sendRequestStopVideoParticipant?: (identity: string) => Promise<boolean>;
    sendRemoveParticipantFromMeeting?: (identity: string) => void;
    sendAssignCoOrganizer?: (identity: string) => void;
    sendMacSharedView?: (label: string, cards: Array<{ id: number; img: string; selected: boolean }>) => void;
    sendEmotionsSharedView?: (label: string, emo: string[]) => void;
    sendSandTherapyOpen?: () => void;
    sendSandTherapyClose?: () => void;
    sendSandSelectFigures?: (
      figures: Array<{ id: string; type: string; color: string; size: string; name: string }>,
    ) => void;
    sendSandStartPlacement?: () => void;
    sendSandPlaceFigure?: (figure: {
      id: string;
      type: string;
      color: string;
      size: string;
      name: string;
      x: number;
      y: number;
      rotation: number;
    }) => void;
    sendSandMoveFigure?: (id: string, x: number, y: number) => void;
    sendSandRotateFigure?: (id: string) => void;
    sendSandResizeFigure?: (id: string, scale: number) => void;
    sendSandRemoveFigure?: (id: string) => void;
    sendSandColorChange?: (color: string) => void;
    sendSandClientReady?: () => void;
    stopScreenShare?: () => void;
    isScreenSharing: boolean;
  };
  chat: {
    isOpen: boolean;
    draft: string;
    messages: MeetChatMessage[];
    isSendingImage?: boolean;
    hasUnread?: boolean;
    disabled?: boolean;
    onDraftChange: (value: string) => void;
    onSend: () => void | Promise<boolean>;
    onImageSelect: (file: File) => void | Promise<boolean>;
    onToggle: () => void;
  };
  toolInbound: {
    colorCodeClientInbound: { nonce: number; selectedColors: string[] } | null;
    onColorCodeClientInboundConsumed: () => void;
    emotionsClientInbound: { nonce: number; result: { category: string[]; emo: string[] } } | null;
    onEmotionsClientInboundConsumed: () => void;
    wheelLiveKitEvent: { nonce: number; kind: 'change' | 'result'; values: number[] } | null;
    onWheelLiveKitEventConsumed: () => void;
    wheelBootstrap: { nonce: number; values: number[] } | null;
    onWheelBootstrapConsumed: () => void;
    psychDpdgInbound: { nonce: number; action: string; data: Record<string, unknown> } | null;
    onPsychDpdgInboundConsumed: () => void;
    clientDpdgBootstrap: { nonce: number; initialSync?: Record<string, unknown> } | null;
    onClientDpdgBootstrapConsumed: () => void;
    clientDpdgInbound: { nonce: number; action: string; data: Record<string, unknown> } | null;
    onClientDpdgInboundConsumed: () => void;
  };
}

export function MeetRoomScene({
  session,
  layout,
  controls,
  supervisor,
  videoRoomActions,
  chat,
  toolInbound,
}: MeetRoomSceneProps) {
  const moderation = useMeetModeration();
  const permissions = useMeetPermissionsContext();
  const tools = useMeetTools();
  const [macPanelExpandedLocal, setMacPanelExpandedLocal] = useState(false);
  const [emotionsPanelExpandedLocal, setEmotionsPanelExpandedLocal] = useState(false);
  const screenShareFeed = getScreenShareFeed(moderation.remoteVideosForMeeting);
  const regularVideos = getRegularVideos(moderation.remoteVideosForMeeting);
  const screenShareOwnerName = getScreenShareOwnerDisplayName(screenShareFeed, regularVideos);
  const participantDisplayNames = getParticipantDisplayNames(regularVideos);
  const totalParticipants = regularVideos.length + (session.localVideo ? 1 : 0);
  const macPanelExpanded = tools.macPanelExpanded || macPanelExpandedLocal;
  const emotionsPanelExpanded = tools.emotionsPanelExpanded || emotionsPanelExpandedLocal;

  useEffect(() => {
    setMacPanelExpandedLocal(tools.macPanelExpanded);
  }, [tools.macPanelExpanded]);

  useEffect(() => {
    setEmotionsPanelExpandedLocal(tools.emotionsPanelExpanded);
  }, [tools.emotionsPanelExpanded]);

  useEffect(() => {
    if (tools.activeTool !== PsyToolsId.MAC_CARDS) {
      setMacPanelExpandedLocal(false);
    }
    if (tools.activeTool !== PsyToolsId.EMOTIONS) {
      setEmotionsPanelExpandedLocal(false);
    }
  }, [tools.activeTool]);
  const visibleConnectionRequests = moderation.visibleConnectionRequests;
  const visibleAwaitingParticipants = moderation.visibleAwaitingParticipants;
  const videoPanelDisplayMode = getVideoPanelDisplayMode({
    activeTool: tools.activeTool,
    breakpoint: layout.breakpoint,
    formFactor: layout.formFactor,
    isPsychologist: session.isPsychologist,
    hasColorCodeActiveTab: tools.hasColorCodeActiveTab,
    cloudsSelectedImage: tools.cloudsSelectedImage,
    cloudsHideVideoForClient: tools.cloudsHideVideoForClient,
    remoteVideosForMeeting: moderation.remoteVideosForMeeting,
    localVideo: session.localVideo,
    screenShareFeed,
  });

  const renderVideoPanel = (displayMode: string) => (
    <VideoPanel
      displayMode={displayMode}
      isPsychologist={session.isPsychologist}
      localVideo={session.localVideo}
      remoteVideos={regularVideos as RemoteFeedObject[]}
      isMyVideoMuted={permissions.isVideoMuted}
      clientName={session.client?.full_name || ''}
      localParticipantIdentity={session.localParticipantIdentity}
      raisedHandParticipantIds={moderation.raisedHandParticipantIds}
      showParticipantNumbers={session.isPsychologist}
      isGroupCall={moderation.isGroupConsultationFlag}
    />
  );

  const renderScreenShareOverlay = () =>
    screenShareFeed ? (
      <ScreenShareViewer
        screenShareFeed={screenShareFeed as RemoteFeedObject}
        psychologistName={session.consultation?.psychologist?.first_name}
        clientName={screenShareOwnerName || session.client?.full_name}
        screenShareOwnerName={screenShareOwnerName}
        isPsychologist={session.isPsychologist}
      />
    ) : null;

  const groupToolScreenShareOverlay = screenShareFeed && tools.activeTool > 0 ? renderScreenShareOverlay() : undefined;

  const toggleMacPanelExpanded = () => {
    const next = !macPanelExpanded;
    setMacPanelExpandedLocal(next);
    tools.setMacPanelExpanded(next);
  };

  const toggleEmotionsPanelExpanded = () => {
    const next = !emotionsPanelExpanded;
    setEmotionsPanelExpandedLocal(next);
    tools.setEmotionsPanelExpanded(next);
  };

  const renderGroupSharedPanel = () => {
    if (!layout.hideToolsPanelForGroupCall) {
      return null;
    }

    if (session.isPsychologist && tools.activeTool === PsyToolsId.MAC_CARDS) {
      return (
        <MeetLayoutVideosTopPanelBottom
          topSectionLarge={totalParticipants > 6}
          bottomClassName="bg-white"
          bottomExpanded={macPanelExpanded}
          onBottomExpandToggle={toggleMacPanelExpanded}
          bottomOverlay={groupToolScreenShareOverlay}
          topContent={renderVideoPanel('grid-screen-share')}
          bottomContent={
            <MacGroupPanel
              onClose={() => {
                tools.setMacPanelExpanded(false);
                setMacPanelExpandedLocal(false);
                controls.closeActiveTool();
              }}
              onConfirm={() => tools.setToolsMenu(PsyToolsId.MAC_CARDS)}
              sendMacCardsOpen={videoRoomActions.sendMacCardsOpen}
              sendMacCardsClose={videoRoomActions.sendMacCardsClose}
              expectedParticipantIds={getExpectedParticipantIds(regularVideos)}
              participantDisplayNames={participantDisplayNames}
              isExpanded={macPanelExpanded}
              onActiveTabChange={videoRoomActions.sendMacSharedView}
            />
          }
        />
      );
    }

    if (!session.isPsychologist && tools.activeTool === PsyToolsId.MAC_CARDS) {
      return (
        <MeetLayoutVideosTopPanelBottom
          topSectionLarge={totalParticipants > 6}
          bottomClassName="bg-white"
          bottomExpanded={tools.macClientPanelExpanded}
          bottomOverlay={groupToolScreenShareOverlay}
          topContent={renderVideoPanel('grid-screen-share')}
          bottomContent={
            <ExpandableClientPanel
              expanded={tools.macClientPanelExpanded}
              onToggle={() => tools.setMacClientPanelExpanded((value) => !value)}
            >
              <MacCardsClient
                participantIdentity={session.localParticipantIdentity}
                sendMacSelectCards={videoRoomActions.sendMacSelectCards}
                isGroupTherapy
              />
            </ExpandableClientPanel>
          }
        />
      );
    }

    if (session.isPsychologist && tools.activeTool === PsyToolsId.EMOTIONS) {
      return (
        <MeetLayoutVideosTopPanelBottom
          topSectionLarge={totalParticipants > 6}
          bottomClassName="bg-white"
          bottomExpanded={emotionsPanelExpanded}
          onBottomExpandToggle={toggleEmotionsPanelExpanded}
          bottomOverlay={groupToolScreenShareOverlay}
          topContent={renderVideoPanel('grid-screen-share')}
          bottomContent={
            <EmotionsGroupPanel
              onClose={() => {
                tools.setEmotionsPanelExpanded(false);
                setEmotionsPanelExpandedLocal(false);
                controls.closeActiveTool();
              }}
              onConfirm={() => tools.setToolsMenu(PsyToolsId.EMOTIONS)}
              sendEmotionsOpen={videoRoomActions.sendEmotionsOpen}
              sendEmotionsClose={videoRoomActions.sendEmotionsClose}
              expectedParticipantIds={getExpectedParticipantIds(regularVideos)}
              participantDisplayNames={participantDisplayNames}
              isExpanded={emotionsPanelExpanded}
              onActiveTabChange={videoRoomActions.sendEmotionsSharedView}
            />
          }
        />
      );
    }

    if (!session.isPsychologist && tools.activeTool === PsyToolsId.EMOTIONS) {
      return (
        <MeetLayoutVideosTopPanelBottom
          topSectionLarge={totalParticipants > 6}
          bottomClassName="bg-white"
          bottomExpanded={tools.emotionsClientPanelExpanded}
          bottomOverlay={groupToolScreenShareOverlay}
          topContent={renderVideoPanel('grid-screen-share')}
          bottomContent={
            <ExpandableClientPanel
              expanded={tools.emotionsClientPanelExpanded}
              onToggle={() => tools.setEmotionsClientPanelExpanded((value) => !value)}
            >
              <EmotionsGroupClient sendEmotionsSelect={videoRoomActions.sendEmotionsSelect ?? (() => undefined)} />
            </ExpandableClientPanel>
          }
        />
      );
    }

    return null;
  };

  const renderToolsPane = () => {
    if (
      layout.hideToolsPanelForGroupCall &&
      tools.activeTool !== PsyToolsId.MAC_CARDS &&
      tools.activeTool !== PsyToolsId.EMOTIONS
    ) {
      return null;
    }

    return (
      <div
        className={classNames(
          'h-full relative',
          {
            '!w-full':
              tools.activeTool === PsyToolsId.DPDG || (layout.breakpoint <= Breakpoint.Sm && tools.activeTool > 0),
          },
          {
            'w-[75%]':
              tools.activeTool === PsyToolsId.WHEEL &&
              (layout.formFactor === 'desktop' || layout.formFactor === 'mobile-landscape'),
            'w-[82%]':
              tools.activeTool === PsyToolsId.SAND_THERAPY &&
              (layout.formFactor === 'desktop' || layout.formFactor === 'mobile-landscape'),
          },
          {
            '!w-full':
              tools.activeTool === PsyToolsId.CLOUDS && !session.isPsychologist && tools.cloudsHideVideoForClient,
          },
          {
            'min-w-[320px] sm:min-w-[400px] flex-shrink-0':
              layout.hideToolsPanelForGroupCall && tools.activeTool === PsyToolsId.MAC_CARDS,
          },
          {
            'min-w-[800px] lg:min-w-[900px]':
              screenShareFeed &&
              tools.activeTool > 0 &&
              tools.activeTool !== PsyToolsId.DPDG &&
              layout.breakpoint > Breakpoint.Sm,
          },
        )}
      >
        <div
          className={classNames('h-full w-full', {
            'opacity-0 pointer-events-none': screenShareFeed && tools.activeTool > 0,
          })}
        >
          {session.isPsychologist ? (
            <MeetToolsPsy
              setResult={(value) => {
                tools.setResult(typeof value === 'function' ? value(tools.result) : value);
              }}
              result={tools.result}
              roomName={session.roomName}
              consultationId={session.consultation?.id}
              users={session.users}
              menu={tools.toolsMenu}
              setMenu={tools.setToolsMenu}
              sendMacCardsOpen={videoRoomActions.sendMacCardsOpen}
              sendMacCardsClose={videoRoomActions.sendMacCardsClose}
              sendEmotionsOpen={videoRoomActions.sendEmotionsOpen}
              sendEmotionsClose={videoRoomActions.sendEmotionsClose}
              emotionsClientInbound={toolInbound.emotionsClientInbound}
              onEmotionsClientInboundConsumed={toolInbound.onEmotionsClientInboundConsumed}
              sendColorCodeOpen={videoRoomActions.sendColorCodeOpen}
              sendColorCodeClose={videoRoomActions.sendColorCodeClose}
              colorCodeClientInbound={toolInbound.colorCodeClientInbound}
              sendWheelOpen={videoRoomActions.sendWheelOpen}
              sendWheelClose={videoRoomActions.sendWheelClose}
              wheelLiveKitEvent={toolInbound.wheelLiveKitEvent}
              onWheelLiveKitEventConsumed={toolInbound.onWheelLiveKitEventConsumed}
              sendDpdgOpen={videoRoomActions.sendDpdgOpen}
              sendDpdgMessage={videoRoomActions.sendDpdgMessage}
              sendDpdgClose={videoRoomActions.sendDpdgClose}
              sendDpdgVideoControl={videoRoomActions.sendDpdgVideoControl}
              psychDpdgInbound={toolInbound.psychDpdgInbound}
              onPsychDpdgInboundConsumed={toolInbound.onPsychDpdgInboundConsumed}
              useDataChannelToolTransport
              onToolActivated={tools.handleToolActivated}
              onColorCodeActiveTabChange={tools.setHasColorCodeActiveTab}
              sendCloudsOpen={() => {
                videoRoomActions.sendCloudsOpen?.();
                tools.setCloudsClientFinished(false);
                tools.setCloudsSelectedImage(null);
                tools.setCloudsLines([]);
              }}
              sendCloudsClose={videoRoomActions.sendCloudsClose}
              clientCloudsLines={tools.cloudsLines}
              clientCloudsSelectedImage={tools.cloudsSelectedImage}
              clientCloudsFinished={tools.cloudsClientFinished}
              onFinishCloudsTool={() => {
                tools.setCloudsClientFinished(false);
                tools.setCloudsSelectedImage(null);
                tools.setCloudsLines([]);
              }}
              sendSandTherapyOpen={() => {
                videoRoomActions.sendSandTherapyOpen?.();
                tools.setSandPlacedFigures([]);
                tools.setSandSelectedFigures([]);
                tools.setSandColor('#fef3c7');
                tools.setSandClientReady(false);
              }}
              sendSandTherapyClose={videoRoomActions.sendSandTherapyClose}
              sandPlacedFigures={tools.sandPlacedFigures}
              sandColor={tools.sandColor}
              sandClientReady={tools.sandClientReady}
            />
          ) : (
            <MeetToolsClient
              roomName={session.roomName}
              users={session.users}
              participantIdentity={session.localParticipantIdentity}
              sendMacSelectCards={videoRoomActions.sendMacSelectCards}
              sendEmotionsSelect={videoRoomActions.sendEmotionsSelect}
              sendWheelChange={videoRoomActions.sendWheelChange}
              sendWheelResult={videoRoomActions.sendWheelResult}
              wheelBootstrap={toolInbound.wheelBootstrap}
              onWheelBootstrapConsumed={toolInbound.onWheelBootstrapConsumed}
              sendColorCodeResult={videoRoomActions.sendColorCodeResult}
              dpdgBootstrap={toolInbound.clientDpdgBootstrap}
              onDpdgBootstrapConsumed={toolInbound.onClientDpdgBootstrapConsumed}
              dpdgInbound={toolInbound.clientDpdgInbound}
              onDpdgInboundConsumed={toolInbound.onClientDpdgInboundConsumed}
              sendDpdgMessage={videoRoomActions.sendDpdgMessage}
              useDataChannelToolTransport
              onCloudsDrawLine={(line) => {
                tools.setCloudsLines((prev) => [...prev, line]);
                videoRoomActions.sendCloudsDrawLine?.(line);
              }}
              onCloudsDrawLines={(lines) => {
                if (lines.length === 0) return;

                tools.setCloudsLines((prev) => [...prev, ...lines]);
                if (videoRoomActions.sendCloudsDrawLines) {
                  videoRoomActions.sendCloudsDrawLines(lines);
                } else {
                  lines.forEach((line) => videoRoomActions.sendCloudsDrawLine?.(line));
                }
              }}
              onCloudsClearDrawing={() => {
                tools.setCloudsLines([]);
                videoRoomActions.sendCloudsClearDrawing?.();
              }}
              onCloudsSelectImage={(imageIndex) => {
                tools.setCloudsSelectedImage(imageIndex);
                tools.setCloudsLines([]);
                tools.setCloudsClientFinished(false);
                videoRoomActions.sendCloudsSelectImage?.(imageIndex);
              }}
              onCloudsClientFinished={() => {
                tools.setCloudsClientFinished(true);
                videoRoomActions.sendCloudsClientFinished?.();
              }}
              onCloudsUndo={(linesCount) => {
                tools.setCloudsLines((prev) => {
                  if (linesCount <= 0 || linesCount > prev.length) {
                    return prev;
                  }

                  return prev.slice(0, prev.length - linesCount);
                });
                videoRoomActions.sendCloudsUndo?.(linesCount);
              }}
              onCloudsHideVideoChange={(hide) => {
                tools.setCloudsHideVideoForClient(hide);
              }}
              onSandSelectFigures={(figures) => {
                tools.setSandSelectedFigures(figures);
                videoRoomActions.sendSandSelectFigures?.(figures);
              }}
              onSandStartPlacement={() => {
                videoRoomActions.sendSandStartPlacement?.();
              }}
              onSandPlaceFigure={(figure) => {
                tools.setSandPlacedFigures((prev) => [...prev, figure]);
                videoRoomActions.sendSandPlaceFigure?.(figure);
              }}
              onSandMoveFigure={(id, x, y) => {
                tools.setSandPlacedFigures((prev) => prev.map((f) => (f.id === id ? { ...f, x, y } : f)));
                videoRoomActions.sendSandMoveFigure?.(id, x, y);
              }}
              onSandRotateFigure={(id) => {
                tools.setSandPlacedFigures((prev) =>
                  prev.map((f) => (f.id === id ? { ...f, rotation: (f.rotation + 45) % 360 } : f)),
                );
                videoRoomActions.sendSandRotateFigure?.(id);
              }}
              onSandResizeFigure={(id, scale) => {
                tools.setSandPlacedFigures((prev) => prev.map((f) => (f.id === id ? { ...f, scale } : f)));
                videoRoomActions.sendSandResizeFigure?.(id, scale);
              }}
              onSandRemoveFigure={(id) => {
                tools.setSandPlacedFigures((prev) => prev.filter((f) => f.id !== id));
                videoRoomActions.sendSandRemoveFigure?.(id);
              }}
              onSandColorChange={(color) => {
                tools.setSandColor(color);
                videoRoomActions.sendSandColorChange?.(color);
              }}
              onSandClientReady={() => {
                tools.setSandClientReady(true);
                videoRoomActions.sendSandClientReady?.();
              }}
              sandPlacedFigures={tools.sandPlacedFigures}
              sandColor={tools.sandColor}
              sandClientReady={tools.sandClientReady}
            />
          )}
        </div>
        {screenShareFeed && tools.activeTool > 0 && (
          <div className="absolute inset-0 z-10">{renderScreenShareOverlay()}</div>
        )}
      </div>
    );
  };

  const shouldHideStandaloneVideo = Boolean(screenShareFeed && tools.activeTool === 0);

  const isGroupMacOrEmotionsTool =
    layout.hideToolsPanelForGroupCall &&
    (tools.activeTool === PsyToolsId.MAC_CARDS || tools.activeTool === PsyToolsId.EMOTIONS);

  /** 2 участника в группе (МАК/эмоции): без второй колонки «Вы» — только верхняя сетка */
  const hideDuoSideVideo = moderation.isGroupConsultationFlag && isGroupMacOrEmotionsTool && totalParticipants < 3;

  /** Демонстрация поверх инструмента, не в правой колонке */
  const hideSideVideoForGroupToolScreenShare = isGroupMacOrEmotionsTool && Boolean(screenShareFeed);

  /** Группа без инструментов: видео на всю ширину, не в «боковой» колонке 1:1 */
  const isGroupStandaloneVideo =
    moderation.isGroupConsultationFlag &&
    tools.activeTool === 0 &&
    !screenShareFeed &&
    !isGroupMacOrEmotionsTool &&
    Boolean(videoPanelDisplayMode);

  /** МАК/эмоции 3+: боковая колонка пустая — не резервировать место справа */
  const hideSideVideoForEmptySidePanel =
    moderation.isGroupConsultationFlag && isGroupMacOrEmotionsTool && !videoPanelDisplayMode;

  const hideSideVideoColumn =
    hideDuoSideVideo ||
    hideSideVideoForGroupToolScreenShare ||
    isGroupStandaloneVideo ||
    hideSideVideoForEmptySidePanel;

  const joinedRowMainContent =
    session.joined &&
    (screenShareFeed && tools.activeTool === 0 ? (
      <MeetLayoutVideosTopPanelBottom
        topSectionLarge={totalParticipants > 6}
        bottomClassName={totalParticipants >= 3 ? 'bg-transparent' : 'bg-grey2'}
        topContent={renderVideoPanel('grid-screen-share')}
        bottomContent={renderScreenShareOverlay()}
      />
    ) : isGroupStandaloneVideo && videoPanelDisplayMode ? (
      renderVideoPanel(videoPanelDisplayMode)
    ) : (
      renderGroupSharedPanel() || renderToolsPane()
    ));

  return (
    <div
      className={classNames(
        'w-full max-w-full flex flex-col gap-[0.25rem] xl:gap-[0.5rem] overflow-x-hidden',
        moderation.isGroupConsultationFlag ? 'py-[0.25rem] xl:py-1' : 'p-[0.25rem] xl:p-1',
        { 'bg-[#1D1B20]': moderation.isGroupConsultationFlag },
      )}
      style={{ height: 'calc(var(--vh, 1vh) * 100)', minWidth: 0 }}
    >
      <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-x-hidden overflow-y-hidden">
        <div
          className={classNames('w-full min-w-0 h-full flex flex-row overflow-x-hidden overflow-y-hidden', {
            'md:gap-[16px]':
              !hideSideVideoColumn &&
              !(tools.activeTool === PsyToolsId.CLOUDS && !session.isPsychologist && tools.cloudsHideVideoForClient),
            'pt-[20px] px-[20px]': moderation.isGroupConsultationFlag,
            relative: moderation.isGroupConsultationFlag,
          })}
        >
          {hideSideVideoColumn ? (
            <div className="flex h-full w-full min-h-0 min-w-0 flex-1 flex-col">{joinedRowMainContent}</div>
          ) : (
            joinedRowMainContent
          )}

          {!(tools.activeTool === PsyToolsId.CLOUDS && !session.isPsychologist && tools.cloudsHideVideoForClient) &&
            !shouldHideStandaloneVideo &&
            !hideSideVideoColumn && (
              <div
                className={classNames(
                  'h-full relative',
                  { 'w-full flex-1 min-w-0': layout.hideToolsPanelForGroupCall },
                  {
                    'w-full':
                      !layout.hideToolsPanelForGroupCall &&
                      tools.activeTool !== PsyToolsId.DPDG &&
                      tools.activeTool !== PsyToolsId.WHEEL &&
                      !(layout.breakpoint <= Breakpoint.Sm && tools.activeTool > 0),
                  },
                  {
                    'w-[25%]':
                      tools.activeTool === PsyToolsId.WHEEL &&
                      (layout.formFactor === 'desktop' || layout.formFactor === 'mobile-landscape'),
                    'w-[18%]':
                      tools.activeTool === PsyToolsId.SAND_THERAPY &&
                      (layout.formFactor === 'desktop' || layout.formFactor === 'mobile-landscape'),
                  },
                  {
                    'w-[0px]':
                      tools.activeTool === PsyToolsId.DPDG ||
                      tools.activeTool === PsyToolsId.WHEEL ||
                      tools.activeTool === PsyToolsId.SAND_THERAPY ||
                      (layout.breakpoint <= Breakpoint.Sm && tools.activeTool > 0),
                  },
                )}
              >
                {session.joined && videoPanelDisplayMode && renderVideoPanel(videoPanelDisplayMode)}
                {session.joined && moderation.remoteVideosForMeeting.length === 0 && session.users.length < 2 && (
                  <div className="pointer-events-none absolute absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <p className="pointer-events-auto text-goldBase text-center text-xl font-comfortaa animate-pulse">
                      {session.isPsychologist ? 'Ожидаем подключение клиента …' : 'Ожидаем подключение психолога …'}
                    </p>
                  </div>
                )}
              </div>
            )}
        </div>

        {moderation.canManageParticipants &&
          moderation.isGroupConsultationFlag &&
          visibleConnectionRequests.length > 0 && (
            <div className="fixed bottom-4 right-4 w-[320px] max-w-[calc(100vw-32px)] h-0 z-[200] pointer-events-none">
              {visibleConnectionRequests.map((participant, index) => (
                <div
                  key={participant.id}
                  className="absolute right-[15px] bottom-[45px] pointer-events-auto"
                  style={{ zIndex: index }}
                >
                  <GroupConnectionRequestNotification
                    displayName={participant.displayName}
                    onAccept={() => moderation.handleAllowConnectionRequest(participant.id)}
                    onDismiss={() => moderation.dismissConnectionRequest(participant.id)}
                  />
                </div>
              ))}
            </div>
          )}

        {moderation.groupParticipantsPanelOpen && moderation.isGroupConsultationFlag && (
          <div className="absolute top-[20px] right-[16px] bottom-0 w-[462px] max-w-[calc(100%-40px)] z-[100]">
            <GroupParticipantsPanel
              onClose={() => moderation.setGroupParticipantsPanelOpen(false)}
              meetLink={window.location.origin + window.location.pathname}
              awaitingCount={visibleAwaitingParticipants.length}
              awaitingParticipants={moderation.canManageParticipants ? visibleAwaitingParticipants : undefined}
              onHideFromAwaiting={moderation.canManageParticipants ? moderation.hideAwaitingParticipant : undefined}
              onMeetingParticipants={moderation.canManageParticipants ? moderation.meetingParticipants : undefined}
              onMuteParticipant={
                moderation.canManageParticipants ? videoRoomActions.sendRequestMuteParticipant : undefined
              }
              onStopVideoParticipant={
                moderation.canManageParticipants ? videoRoomActions.sendRequestStopVideoParticipant : undefined
              }
              onMuteAll={
                moderation.canManageParticipants &&
                videoRoomActions.sendRequestMuteParticipant &&
                moderation.remoteVideosForMeeting.length > 0
                  ? () => {
                      moderation.meetingParticipants.forEach((participant) => {
                        if (participant.id && (session.isPsychologist || !participant.isPsychologist)) {
                          videoRoomActions.sendRequestMuteParticipant?.(participant.id);
                        }
                      });
                    }
                  : undefined
              }
              onMeetingCount={moderation.canManageParticipants ? moderation.remoteVideosForMeeting.length : 0}
              onAllow={moderation.canManageParticipants ? moderation.handleAllowConnectionRequest : undefined}
              onAllowAll={moderation.canManageParticipants ? moderation.allowAllConnections : undefined}
              onMakeCoOrganizer={session.isPsychologist ? videoRoomActions.sendAssignCoOrganizer : undefined}
              canAssignCoOrganizer={session.isPsychologist}
              onRemoveParticipant={
                moderation.canManageParticipants ? videoRoomActions.sendRemoveParticipantFromMeeting : undefined
              }
            />
          </div>
        )}
      </div>

      <div className={classNames('h-[57px] w-full shrink-0', moderation.isGroupConsultationFlag && 'bg-[#000000]')}>
        <SettingPanel
          isGroupCall={moderation.isGroupConsultationFlag}
          isGroupConsultation={moderation.isGroupConsultationFlag}
          statusData={supervisor.statusActive}
          isClientConnected={session.isClientConnected}
          isBackgroundSupported={videoRoomActions.isBackgroundSupported}
          backgroundMode={videoRoomActions.backgroundMode}
          onBackgroundBlurClick={controls.onBackgroundBlurClick}
          leaveRoom={controls.closeMeet}
          onEndConferenceForAll={
            moderation.isGroupConsultationFlag && session.isPsychologist ? controls.closeMeet : undefined
          }
          onLeaveConference={
            moderation.isGroupConsultationFlag && session.isPsychologist ? controls.leaveConferenceOnly : undefined
          }
          audioMute={permissions.shouldShowAudioRed}
          videoMute={permissions.shouldShowVideoRed}
          isPsychologist={session.isPsychologist}
          isCoOrganizer={moderation.isCoOrganizer}
          isTooltipVisible={supervisor.isTooltipVisible}
          isSupervisorAllowed={supervisor.isSupervisorAllowed}
          supervisorRemaining={supervisor.supervisorRemaining}
          isSupervisorBalanceWarning={supervisor.isSupervisorBalanceWarning}
          onAudioMuteClick={permissions.toggleMuteAudio}
          onVideoMuteClick={permissions.toggleMuteVideo}
          onOpenSettingsClick={controls.onOpenSettingsClick}
          showMicWarning={permissions.showMicWarning}
          showCamWarning={permissions.showCamWarning}
          isLocalActiveSpeaker={session.isLocalActiveSpeaker}
          onSupervisorClick={controls.onSupervisorClick}
          supervisorNotification={supervisor.supervisorNotification}
          onCloseNotification={() => supervisor.setSupervisorNotification(null)}
          supervisorSend={supervisor.supervisorSend}
          sendSupervisorResponse={videoRoomActions.sendSupervisorResponse}
          setSupervisorSend={supervisor.setSupervisorSend}
          isNotificationClosed={supervisor.notificationClosed}
          onNotificationClose={() => supervisor.setNotificationClosed(true)}
          isSupervisorLoading={supervisor.isSupervisorLoading}
          startScreenShare={controls.onScreenShareClick}
          stopScreenShare={videoRoomActions.stopScreenShare}
          isScreenSharing={videoRoomActions.isScreenSharing}
          onMacCardsClick={
            layout.hideToolsPanelForGroupCall && session.isPsychologist
              ? controls.onOpenMacCardsForGroup
              : controls.onOpenMacDeckModal
          }
          onGroupParticipantsClick={() => moderation.setGroupParticipantsPanelOpen(true)}
          onRaisedHandClick={controls.onToggleRaisedHand}
          groupParticipantsPanelOpen={moderation.groupParticipantsPanelOpen}
          groupParticipantsAwaitingCount={moderation.awaitingCount}
          onGroupMoreOptionEmotions={
            moderation.isGroupConsultationFlag && session.isPsychologist ? controls.onOpenEmotionsForGroup : undefined
          }
          onGroupMoreOptionMacCards={
            moderation.isGroupConsultationFlag && session.isPsychologist ? controls.onOpenMacCardsForGroup : undefined
          }
          isRaisedHandActive={moderation.isLocalHandRaised}
          showRaisedHandButton={moderation.isGroupConsultationFlag}
          chatPanelOpen={chat.isOpen}
          chatHasUnread={chat.hasUnread}
          onChatClick={chat.onToggle}
        />
      </div>

      <MeetChatPanel
        isOpen={chat.isOpen}
        draft={chat.draft}
        messages={chat.messages}
        isSendingImage={chat.isSendingImage}
        disabled={chat.disabled}
        onDraftChange={chat.onDraftChange}
        onSend={chat.onSend}
        onImageSelect={chat.onImageSelect}
        onClose={chat.onToggle}
      />
    </div>
  );
}
